import CampaignStatusBadge from './CampaignStatusBadge';
import { campaignTitle } from '../utils/campaignTitle';
import { formatSastAsWritten, formatUtcAsSast } from '../utils/sastDateTime';
import type { Campaign } from '../types/whatsapp';

const NOTHING_TO_SHOW = '—';

const HEADING_STYLE =
  'px-4 py-3 text-left text-xs font-light uppercase tracking-wider text-gray-700';
const CELL_STYLE =
  'whitespace-nowrap px-4 py-3 text-xs font-light text-gray-700';
const ACTION_STYLE = 'text-xs font-light hover:underline disabled:opacity-50';

interface CampaignTableProps {
  campaigns: Campaign[];
  cancellingCampaignId: string | null;
  fetchingCsvCampaignId: string | null;
  onCancel: (campaign: Campaign) => void;
  onDownloadCsv: (campaignId: string) => void;
  onView: (campaign: Campaign) => void;
}

function sentOrScheduledDate(campaign: Campaign): string {
  if (campaign.dispatched_at) return formatUtcAsSast(campaign.dispatched_at);
  if (campaign.send_at) return formatSastAsWritten(campaign.send_at);
  return NOTHING_TO_SHOW;
}

function acceptedOfTotal(campaign: Campaign): string {
  if (campaign.total_messages == null) return NOTHING_TO_SHOW;
  if (campaign.sent_count == null) {
    return `${NOTHING_TO_SHOW} / ${campaign.total_messages}`;
  }
  return `${campaign.sent_count} / ${campaign.total_messages}`;
}

export default function CampaignTable({
  campaigns,
  cancellingCampaignId,
  fetchingCsvCampaignId,
  onCancel,
  onDownloadCsv,
  onView,
}: CampaignTableProps) {
  return (
    <div className="overflow-hidden rounded-lg border border-gray-200">
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className={HEADING_STYLE}>Comms</th>
              <th className={HEADING_STYLE}>Template</th>
              <th className={HEADING_STYLE}>Date (SAST)</th>
              <th className={HEADING_STYLE}>Status</th>
              <th className={HEADING_STYLE}>Accepted / total</th>
              <th className={HEADING_STYLE}>Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 bg-white">
            {campaigns.map((campaign) => {
              const isFetchingCsv =
                fetchingCsvCampaignId === campaign.campaign_id;
              const isCancelling =
                cancellingCampaignId === campaign.campaign_id;

              return (
                <tr key={campaign.campaign_id} className="hover:bg-gray-50">
                  <td className={`${CELL_STYLE} text-gray-900`}>
                    {campaignTitle(campaign)}
                  </td>
                  <td className={CELL_STYLE}>
                    {campaign.template_name || NOTHING_TO_SHOW}
                  </td>
                  <td className={CELL_STYLE}>
                    {sentOrScheduledDate(campaign)}
                  </td>
                  <td className="whitespace-nowrap px-4 py-3">
                    <CampaignStatusBadge status={campaign.status} />
                  </td>
                  <td className={CELL_STYLE}>{acceptedOfTotal(campaign)}</td>
                  <td className="whitespace-nowrap px-4 py-3">
                    <span className="space-x-3 whitespace-nowrap">
                      {campaign.template_name && (
                        <button
                          type="button"
                          onClick={() => onView(campaign)}
                          aria-label={`View the message for ${campaignTitle(campaign)}`}
                          className={`${ACTION_STYLE} text-blue-600`}
                        >
                          View
                        </button>
                      )}
                      {campaign.has_csv && (
                        <button
                          type="button"
                          onClick={() => onDownloadCsv(campaign.campaign_id)}
                          disabled={isFetchingCsv}
                          aria-busy={isFetchingCsv}
                          aria-label={`Download the sheet for ${campaignTitle(campaign)}`}
                          className={`${ACTION_STYLE} text-blue-600`}
                        >
                          CSV
                        </button>
                      )}
                      {campaign.can_cancel && (
                        <button
                          type="button"
                          onClick={() => onCancel(campaign)}
                          disabled={isCancelling}
                          aria-busy={isCancelling}
                          aria-label={`Cancel ${campaignTitle(campaign)}`}
                          className={`${ACTION_STYLE} text-red-600`}
                        >
                          Cancel
                        </button>
                      )}
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
